const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : null;
};

const parseResponse = async (response) => {
  let body = null;
  try {
    body = await response.json();
  } catch (e) {
    body = null;
  }

  if (!response.ok) {
    const message = (body && body.error) || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return body;
};

export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);

  return fetch("/playground/invoice_pipeline/upload", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    body: formData,
  }).then(parseResponse);
};

// Step 1's main upload: one or more files. Multiple is only meaningful
// when the first is an image (see the server-side check in
// Playground::InvoicePipelineController#upload) -- a scanned invoice
// captured as several separate photos, collated into one document at
// Step 4.1.
export const uploadFiles = (files) => {
  const formData = new FormData();
  Array.from(files).forEach((file) => formData.append("files[]", file));

  return fetch("/playground/invoice_pipeline/upload", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    body: formData,
  }).then(parseResponse);
};

// Step 1's alternative source: unencoded Chouxbox file groups at the
// selected location's restaurant (read-only listing).
export const fetchChouxboxFileGroups = (accountId, locationId, page = 1) =>
  fetchJSON(`chouxbox_file_groups?${new URLSearchParams({ account_id: accountId, location_id: locationId, page }).toString()}`);

// Downloads one file group into the pipeline; answers exactly like uploadFiles.
export const importChouxboxFileGroup = (accountId, locationId, fileGroupId) => {
  return fetch("/playground/invoice_pipeline/import_chouxbox_file_group", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ account_id: accountId, location_id: locationId, file_group_id: fileGroupId }),
  }).then(parseResponse);
};

export const runStep = (path, fileToken) => {
  return fetch(`/playground/invoice_pipeline/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ file_token: fileToken }),
  }).then(parseResponse);
};

export const fetchJSON = (path) => {
  return fetch(`/playground/invoice_pipeline/${path}`).then(parseResponse);
};

export const searchAccounts = (query) =>
  fetchJSON(`search_accounts?${new URLSearchParams({ q: query }).toString()}`);

export const searchLocations = (accountId, query) =>
  fetchJSON(`search_locations?${new URLSearchParams({ account_id: accountId, q: query }).toString()}`);

export const mapData = (fileToken, accountId, locationId) => {
  return fetch("/playground/invoice_pipeline/map_data", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ file_token: fileToken, account_id: accountId, location_id: locationId }),
  }).then(parseResponse);
};

export const validateAndScore = (fileToken, accountId, locationId) => {
  return fetch("/playground/invoice_pipeline/validate_and_score", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ file_token: fileToken, account_id: accountId, location_id: locationId }),
  }).then(parseResponse);
};

const vendorSpecsPost = (path, params) => {
  return fetch(`/playground/vendor_parser_specs/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(params),
  }).then(parseResponse);
};

const vendorSpecsGet = (path, params) => {
  const query = params ? `?${new URLSearchParams(params).toString()}` : "";
  return fetch(`/playground/vendor_parser_specs/${path}${query}`).then(parseResponse);
};

export const fetchVendorGroups = () => vendorSpecsGet("vendor_groups");

export const renameVendorGroup = (layoutId, vendorName) =>
  vendorSpecsPost("rename_vendor_group", { id: layoutId, vendor_name: vendorName });

export const generateVendorSpec = (vendorName) =>
  vendorSpecsPost("generate", { vendor_name: vendorName });

export const fetchVendorSpec = (vendorKey) => vendorSpecsGet("show_spec", { vendor_key: vendorKey });

export const testVendorSpecUpload = (vendorKey, fileToken) =>
  vendorSpecsPost("test_upload", { vendor_key: vendorKey, file_token: fileToken });

export const promoteVendorSpec = (vendorKey) => vendorSpecsPost("promote", { vendor_key: vendorKey });
